import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions } from '@onefootprint/fetch-dashboard';
import type { LitePlaybook } from '@onefootprint/request-types/dashboard';
import { LinkButton, type LinkButtonVariant } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useGetPlaybookVersionDisplayName } from '@/hooks/use-get-playbook-version-display-name';
import { useOpenPage } from '@/hooks/use-open-page';
import useEntityId from '../entities/components/details/hooks/use-entity-id';
import useEntityKind from '../entities/components/details/hooks/use-entity-kind';
import { ACTIVE_NODE_QUERY_KEY } from '../playbook-builder/playbook-builder.constants';
type OnboardingTraceProps = {
  /** When provided, fetches and shows the related onboarding trace. */
  onboardingId?: string;
  /** When provided, also links to the playbook for this trace. */
  playbook?: Pick<LitePlaybook, 'playbookId' | 'name' | 'version' | 'id' | 'isHidden'>;
  label?: string;
  nodeId?: string;
  className?: string;
  variant?: LinkButtonVariant;
  /**
   * Skips the trace fetch when `false` — that request 400s for an onboarding with no graph. Pass
   * `EntityOnboarding.hasGraphTrace` where it's known; omit it when it isn't, which fetches.
   */
  hasGraphTrace?: boolean;
};

/** When clicked, shows the execution trace for the `onboardingId` if it exists. Otherwise, navigates to the playbook details page for the `playbook`, if provided. */
export const OnboardingTraceLink = t0 => {
  const $ = _c(28);
  const {
    onboardingId,
    playbook,
    label,
    nodeId,
    className,
    variant: t1,
    hasGraphTrace
  } = t0;
  const variant = t1 === undefined ? "label-2" : t1;
  const fpId = useEntityId();
  const entityKind = useEntityKind();
  const handleOpenPage = useOpenPage();
  const {
    getPlaybookVersionDisplayName
  } = useGetPlaybookVersionDisplayName();
  const t2 = onboardingId ?? "";
  let t3;
  if ($[0] !== fpId || $[1] !== t2) {
    t3 = getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions({
      path: {
        fpId,
        onboardingId: t2
      }
    });
    $[0] = fpId;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = !!onboardingId && hasGraphTrace !== false;
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = {
      ...t3,
      enabled: t4
    };
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    data: trace
  } = useQuery(t5);
  let t6;
  if ($[6] !== entityKind?.urlPath || $[7] !== fpId || $[8] !== handleOpenPage || $[9] !== nodeId || $[10] !== onboardingId || $[11] !== playbook || $[12] !== trace) {
    t6 = event => {
      if (onboardingId && trace) {
        const query = nodeId ? {
          [ACTIVE_NODE_QUERY_KEY]: nodeId
        } : undefined;
        handleOpenPage(event, `/${entityKind?.urlPath}/${fpId}/onboardings/${onboardingId}/trace`, query);
      } else {
        if (playbook) {
          const {
            playbookId,
            id
          } = playbook;
          const qs = playbook.isHidden ? undefined : {
            version: id
          };
          handleOpenPage(event, `/playbooks/${playbookId}`, qs);
        }
      }
    };
    $[6] = entityKind?.urlPath;
    $[7] = fpId;
    $[8] = handleOpenPage;
    $[9] = nodeId;
    $[10] = onboardingId;
    $[11] = playbook;
    $[12] = trace;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleClick = t6;
  let t7;
  if ($[14] !== getPlaybookVersionDisplayName || $[15] !== label || $[16] !== playbook || $[17] !== trace?.kind) {
    t7 = () => {
      if (label) {
        return label;
      }
      return getPlaybookVersionDisplayName(playbook, trace?.kind);
    };
    $[14] = getPlaybookVersionDisplayName;
    $[15] = label;
    $[16] = playbook;
    $[17] = trace?.kind;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const getLabel = t7;
  let t8;
  if ($[19] !== className) {
    t8 = cx("max-md:max-w-full max-md:whitespace-normal max-md:text-left", className);
    $[19] = className;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== getLabel) {
    t9 = getLabel();
    $[21] = getLabel;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== handleClick || $[24] !== t8 || $[25] !== t9 || $[26] !== variant) {
    t10 = <LinkButton variant={variant} className={t8} onClick={handleClick}>{t9}</LinkButton>;
    $[23] = handleClick;
    $[24] = t8;
    $[25] = t9;
    $[26] = variant;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
};